import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { gerarParcelas } from '@core/util/parcelaUtil';
import { cancelarAlert } from 'app/services/alerts';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect } from 'react';
import { FaShoppingCart } from 'react-icons/fa';
import * as Yup from 'yup';
import { CompraContext } from '../../../CompraContext';
import {
  useAproveitamentoCreditoInitialValue,
  useCategoriasInitialValue,
  useCodigoInitialValue,
  useCompraServicoInitialValue,
  useFornecedorInitialValue,
  usePagamentoInitialValue,
  useValorLiquidoInitialValue,
} from '../compraInitialValues';
import { compraModais } from '../compraModais';
import {
  useAproveitamentoCreditoSchema,
  useCategoriasSchema,
  useFornecedorSchema,
  usePagamentoSchema,
} from '../compraSchema';
import CompraServicoForm from './CompraServicoForm';

const HOJE = moment();

const CompraServicoModal = ({ onSubmit, empresaIbsCbs = false }) => {
  const [compra] = useContext(CompraContext).compraAtual;
  const [modal, setModal] = useContext(CompraContext).modal;
  const [search] = useContext(CompraContext).search;
  const [page] = useContext(CompraContext).page;
  const [size] = useContext(CompraContext).size;
  const [sort] = useContext(CompraContext).sort;

  const initialValues = {
    id: compra?.id || '',
    valorLiquido: useValorLiquidoInitialValue(compra),
    codigo: useCodigoInitialValue(compra?.compraId),
    dataCompra: compra?.dataCompra ? moment(compra.dataCompra) : HOJE,
    fornecedor: useFornecedorInitialValue(compra),
    categorias: useCategoriasInitialValue(compra),
    compraServico: useCompraServicoInitialValue(compra?.compraServico),
    pagamento: usePagamentoInitialValue(compra),
    anexos: compra?.anexos || [],
    observacao: compra?.observacao,
    aproveitamentoCredito: useAproveitamentoCreditoInitialValue(compra),
  };

  const servicoSchema = Yup.object().shape({
    dataCompra: Yup.object()
      .nullable()
      .required(mensagemCampoObrigatorio('a data da compra')),
    fornecedor: useFornecedorSchema(),
    categorias: useCategoriasSchema(),
    pagamento: usePagamentoSchema(),
    compraServico: Yup.object().shape({
      notaFiscal: Yup.object().shape({
        dataEmissao: Yup.object().nullable(),
        retencoes: Yup.array().of(
          Yup.object().shape({
            imposto: Yup.object()
              .nullable()
              .required(mensagemCampoObrigatorio('o imposto')),
            valor: Yup.number()
              .nullable()
              .required(mensagemCampoObrigatorio('o valor')),
            dataVencimento: Yup.object()
              .nullable()
              .required(mensagemCampoObrigatorio('a data de vencimento')),
          })
        ),
      }),
    }),
    aproveitamentoCredito: useAproveitamentoCreditoSchema(),
  });

  const close = () => {
    formik.resetForm(initialValues);
    setModal('');
  };

  const handleSubmit = async (data = {}) => {
    if (!formik?.dirty) {
      close();
      return;
    }

    const compra = JSON.parse(JSON.stringify(data));

    compra.fornecedorId = compra?.fornecedor?.id;
    compra.categorias.map((categoria) => {
      categoria.classeId = categoria.classe.id;
      return categoria;
    });

    if (compra.pagamento?.avista) {
      compra.pagamento.avista.contaId = compra.pagamento.avista.conta?.id;
      delete compra.pagamento.avista.conta;
    }

    if (compra.pagamento?.parcelado) {
      if (!compra.pagamento.parcelado.parcelas) {
        const quantidade = compra?.pagamento?.parcelado?.quantidade || 0;
        const dataPrimeiraParcela = compra?.dataCompra ? moment(compra.dataCompra) : moment();

        const { valorLiquido } = compra;

        compra.pagamento.parcelado = gerarParcelas(quantidade, dataPrimeiraParcela, valorLiquido);
      } else {
        compra.pagamento.parcelado = compra.pagamento.parcelado.parcelas;
      }
    }

    if (compra.compraServico?.notaFiscal?.municipio) {
      compra.compraServico.notaFiscal.municipioId = compra.compraServico.notaFiscal.municipio.id;
      delete compra.compraServico.notaFiscal.municipio;
    }

    if (!compra.compraServico?.notaFiscal?.temRetencao) {
      delete compra.compraServico.notaFiscal.retencoes;
    } else if (compra.compraServico?.notaFiscal?.retencoes) {
      compra.compraServico.retencoes = compra.compraServico?.notaFiscal?.retencoes.map((retencao) => ({
        id: retencao?.id,
        impostoId: retencao?.imposto?.id,
        valor: retencao?.valor,
        dataVencimento: retencao?.dataVencimento,
        contribuinte: retencao?.contribuinte,
      }));
      delete compra.compraServico?.notaFiscal?.retencoes;
    }

    if (compra.pagamento?.condicaoPagamento === 'A_VISTA') {
      delete compra.pagamento.parcelado;

      if (!compra?.pagamento?.avista?.compraPaga) {
        delete compra.pagamento.avista.dataPagamento;
        delete compra.pagamento.avista.contaId;
        delete compra.pagamento.avista.formaPagamento;
        delete compra.pagamento.avista.numeroCheque;
        delete compra.pagamento.avista.dataLiquidacao;
      }
    } else {
      delete compra.pagamento.avista;
    }

    // Apaga dados que a api não precisa
    delete compra.fornecedor;
    delete compra.categoria;

    const res = await onSubmit(compra, { q: search, page, size, sort }, 'SERVICO');

    if (res) {
      close();
    }
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const formik = useFormik({
    validationSchema: servicoSchema,
    initialValues,
    onSubmit: handleSubmit,
    validateOnChange: true,
  });

  useEffect(() => {
    formik.resetForm(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compra]);

  return (
    <SimpleDialog fullScreen open={modal === compraModais.comprarServico} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Compra de serviço',
            icon: <FaShoppingCart />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full">
              <CompraServicoForm empresaIbsCbs={empresaIbsCbs} />
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CompraServicoModal;
